import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { ButtonConfig } from '../../../types';
import { EzTextService, TimeZone } from '../../ez-text.service';
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { DocMeta } from "@template-ui/decorators";

interface TextTemplateTestCase {
  id: string;
  category: 'String' | 'Number' | 'DateTime';
  comments: string;
  data: string;
  template: string;
  expectedOutput: string;
  actualOutput: string;
  didPass: boolean;
}

@DocMeta({
  showcaseTitle: "EZ Text Tests",
  showcaseDesc: "This sample executes a bunch of tests and formats a markdown table with the results, which is rendered below. The markdown can be copied to the clipboard and used elsewhere, for example pasting into an AI tool to help figure out failed tests.",
  hide: true
})
@Component({
    selector: 'ui-text-template-test-runner, ez-text-template-test-runner',
    imports: [CommonModule, MarkdownModule, UiLibraryModule],
    templateUrl: './ez-text-test-runner.component.html',
    styleUrl: './ez-text-test-runner.component.scss'
})
export class EzTextTestRunnerComponent {
  private readonly initialTests: TextTemplateTestCase[] = [
    {
      id: 'basic',
      category: 'String',
      comments: 'Basic token replacement',
      data: JSON.stringify({ name: 'World' }, null, 2),
      template: 'Hello {{name}}',
      expectedOutput: 'Hello World',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'uppercase',
      category: 'String',
      comments: 'Uppercase transformation',
      data: JSON.stringify({ status: 'on hold' }, null, 2),
      template: '{{status | upper}}',
      expectedOutput: 'ON HOLD',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'titleCase',
      category: 'String',
      comments: 'Naive title-case illustrates particles get capitalized',
      data: JSON.stringify({ name: 'luna de la cruz' }, null, 2),
      template: '{{name | title}}',
      expectedOutput: 'Luna De La Cruz',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'smartTitleCase',
      category: 'String',
      comments: 'Smart title-case keeps lowercase particles',
      data: JSON.stringify({ name: 'luna de la cruz' }, null, 2),
      template: '{{name | smartTitleCase}}',
      expectedOutput: 'Luna de la Cruz',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'smartTitleCaseIrish',
      category: 'String',
      comments: "Smart title-case handles apostrophes (O'Reilly)",
      data: JSON.stringify({ name: "aoife o'reilly" }, null, 2),
      template: '{{name | smartTitleCase}}',
      expectedOutput: "Aoife O'Reilly",
      actualOutput: '',
      didPass: false
    },
    {
      id: 'smartTitleCaseScottish',
      category: 'String',
      comments: 'Smart title-case fixes Mc/Mac prefixes',
      data: JSON.stringify({ name: 'seamus macalister' }, null, 2),
      template: '{{name | smartTitleCase}}',
      expectedOutput: 'Seamus MacAlister',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'smartTitleCaseHyphenated',
      category: 'String',
      comments: 'Smart title-case handles hyphenated names',
      data: JSON.stringify({ name: 'marie-claire de la rosa' }, null, 2),
      template: '{{name | smartTitleCase}}',
      expectedOutput: 'Marie-Claire de la Rosa',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'camelCase',
      category: 'String',
      comments: 'CamelCase conversion',
      data: JSON.stringify({ label: 'assigned investigator' }, null, 2),
      template: '{{label | camel}}',
      expectedOutput: 'assignedInvestigator',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'kebab',
      category: 'String',
      comments: 'Kebab-case conversion',
      data: JSON.stringify({ title: 'Orion Launch Sequence' }, null, 2),
      template: '{{title | kebab}}',
      expectedOutput: 'orion-launch-sequence',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'snake',
      category: 'String',
      comments: 'Snake_case conversion',
      data: JSON.stringify({ title: 'ORION_LAUNCH_SEQUENCE' }, null, 2),
      template: '{{title | snake}}',
      expectedOutput: 'orion_launch_sequence',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'screamingSnake',
      category: 'String',
      comments: 'SCREAMING_SNAKE conversion',
      data: JSON.stringify({ title: 'Orion Launch Sequence' }, null, 2),
      template: '{{title | screamingSnake}}',
      expectedOutput: 'ORION_LAUNCH_SEQUENCE',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'screamingToKebab',
      category: 'String',
      comments: 'Convert SCREAMING_SNAKE to kebab-case for URL slugs',
      data: JSON.stringify({ code: 'MISSION_ALPHA_READY' }, null, 2),
      template: '{{code | kebab}}',
      expectedOutput: 'mission-alpha-ready',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'currency',
      category: 'Number',
      comments: 'Currency with locale override',
      data: JSON.stringify({ amount: 1234.56 }, null, 2),
      template: 'Balance {{amount | currency("EUR","de-DE")}}',
      expectedOutput: 'Balance 1.234,56 �',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'percent',
      category: 'Number',
      comments: 'Percent formatting',
      data: JSON.stringify({ ratio: 0.45 }, null, 2),
      template: 'Completion {{ratio | percent}}',
      expectedOutput: 'Completion 45%',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'unicodeDecimalPattern',
      category: 'Number',
      comments: 'Unicode CLDR decimal pattern "#,##0.00"',
      data: JSON.stringify({ amount: 1234567.89 }, null, 2),
      template: '{{amount | "#,##0.00"}}',
      expectedOutput: '1,234,567.89',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'unicodeCurrencyPattern',
      category: 'Number',
      comments: 'Unicode CLDR currency pattern "#,##0.00 ¤" (USD, en-US)',
      data: JSON.stringify({ amount: 1234567.89 }, null, 2),
      template: '{{amount | "#,##0.00 ¤"}}',
      expectedOutput: '$1,234,567.89',
      actualOutput: '',
      didPass: false
    },

    // Does not support passing multiple arguments with colon, use number().
    // {
    //   id: 'unicodeCurrencyPatternEuroGerman',
    //   category: 'Number',
    //   comments: 'Unicode CLDR currency pattern "#,##0.00 ¤" (EUR, de-DE)',
    //   data: JSON.stringify({ amount: 1234567.89 }, null, 2),
    //   template: `{{amount | "#,##0.00 ¤" : 'EUR' : 'de-DE'}}`,
    //   expectedOutput: '1.234.567,89€',
    //   actualOutput: '',
    //   didPass: false
    // },
    {
      id: 'unicodeCurrencyPatternEuroGermanFunc',
      category: 'Number',
      comments: 'Unicode CLDR currency pattern using number() function with EUR and de-DE',
      data: JSON.stringify({ amount: 1234567.89 }, null, 2),
      //template: `{{amount | number("#,##0.00 ¤", "EUR", "de-DE")}}`,
      template: `{{amount | number("#,##0.00 ¤", EUR, de-DE)}}`,
      expectedOutput: '1.234.567,89 �',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'unicodePercentPattern',
      category: 'Number',
      comments: 'Unicode CLDR percent pattern "#,##0%"',
      data: JSON.stringify({ ratio: 0.45 }, null, 2),
      template: '{{ratio | "#,##0%"}}',
      expectedOutput: '45%',
      actualOutput: '',
      didPass: false
    },
    // --- DATE TESTS GROUPED BELOW ---
    {
      id: 'dateFormat',
      category: 'DateTime',
      comments: 'Calendar date formatting without TZ shift',
      data: JSON.stringify({ meeting: '2025-03-15T00:00:00Z' }, null, 2),
      template: 'Meeting {{meeting | MMM d, yyyy}}',
      expectedOutput: 'Meeting Mar 15, 2025',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'timezone',
      category: 'DateTime',
      comments: 'tz() opt-in with explicit zone',
      data: JSON.stringify({ releaseTime: '2025-01-05T18:00:00Z' }, null, 2),
      template: `LA {{ releaseTime | tz('${TimeZone.LosAngeles}',"MMM d, yyyy h:mm a") }}`,
      expectedOutput: 'LA Jan 5, 2025 10:00 AM',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'utcFormat',
      category: 'DateTime',
      comments: 'utc() opt-in preserving UTC clock time',
      data: JSON.stringify({ releaseTime: '2025-01-05T18:00:00Z' }, null, 2),
      template: `UTC {{ releaseTime | utc("MMM d, yyyy HH:mm 'UTC'") }}`,
      expectedOutput: 'UTC Jan 5, 2025 18:00 UTC',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'localTimeOptIn',
      category: 'DateTime',
      comments: `local() opt-in shifts UTC to viewer local time (this test will only pass in ${TimeZone.LosAngeles})`,
      data: JSON.stringify({ meeting: '2025-03-15T15:00:00Z' }, null, 2),
      template: 'Local time {{meeting | local("yyyy-MM-dd HH:mm")}}',
      expectedOutput: `Local time 2025-03-15 08:00`,
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dayOfMonthLowercase',
      category: 'DateTime',
      comments: 'Lowercase d token returns calendar day-of-month',
      data: JSON.stringify({ date: '2025-01-05T12:00:00Z' }, null, 2),
      template: 'Day-of-month {{ date | dd }}',
      expectedOutput: 'Day-of-month 05',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dayOfYearUppercase',
      category: 'DateTime',
      comments: 'Uppercase D token returns day-of-year (1-366)',
      data: JSON.stringify({ date: '2025-01-05T12:00:00Z' }, null, 2),
      template: 'Day-of-year {{ date | DDD }}',
      expectedOutput: 'Day-of-year 005',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'calendarYearLowercase',
      category: 'DateTime',
      comments: 'Lowercase yyyy returns calendar year number',
      data: JSON.stringify({ date: '2016-01-01T00:00:00Z' }, null, 2),
      template: 'Calendar year {{ date | yyyy }}',
      expectedOutput: 'Calendar year 2016',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'weekYearUppercase',
      category: 'DateTime',
      comments: 'Uppercase YYYY returns ISO week-numbering year',
      data: JSON.stringify({ date: '2016-01-01T00:00:00Z' }, null, 2),
      template: 'Week-numbering year {{ date | YYYY }}',
      expectedOutput: 'Week-numbering year 2015',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'weekYearUppercaseOptOut',
      category: 'DateTime',
      comments: 'Uppercase YYYY with ! suppresses warning while keeping ISO week-year',
      data: JSON.stringify({ date: '2016-01-01T00:00:00Z' }, null, 2),
      template: 'Week-year acknowledged {{ date | YYYY! }}',
      expectedOutput: 'Week-year acknowledged 2015',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dayOfYearUppercaseOptOut',
      category: 'DateTime',
      comments: 'Uppercase DDD with ! suppresses warning while keeping day-of-year',
      data: JSON.stringify({ date: '2025-08-05T12:00:00Z' }, null, 2),
      template: 'Day-of-year acknowledged {{ date | DDD! }}',
      expectedOutput: 'Day-of-year acknowledged 217',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dateOnlyIso',
      category: 'DateTime',
      comments: 'Date-only ISO string (YYYY-MM-DD) should be treated as midnight UTC',
      data: JSON.stringify({ date: '2025-03-15' }, null, 2),
      template: 'Date {{ date | MMM dd, yyyy}} (date not shifted)',
      expectedOutput: 'Date Mar 15, 2025 (date not shifted)',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dateOnlyIsoLocal',
      category: 'DateTime',
      comments: 'Date-only ISO string with local() opt-in shifts to viewer local time (this test will only pass in America/Los_Angeles)',
      data: JSON.stringify({ date: '2025-03-15' }, null, 2),
      template: 'Local: {{ date | local("MMM dd, yyyy HH:mm") }} (date shifted)',
      expectedOutput: 'Local: Mar 14, 2025 17:00 (date shifted)',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'dateOnlyIsoTz',
      category: 'DateTime',
      comments: 'Date-only ISO string with tz() opt-in for America/New_York',
      data: JSON.stringify({ date: '2025-03-15' }, null, 2),
      template: `NY date {{ date | tz('${TimeZone.NewYork}',"yyyy-MM-dd HH:mm") }}`,
      expectedOutput: 'NY date 2025-03-14 20:00',
      actualOutput: '',
      didPass: false
    }
    ,
    {
      id: 'prettyJsonObject',
      category: 'String',
      comments: 'Pretty prints a JS object as JSON',
      data: JSON.stringify({ foo: 1, bar: [2, 3] }, null, 2),
      template: '{{fooBar | prettyJson}}',
      expectedOutput: '{\n  "foo": 1,\n  "bar": [\n    2,\n    3\n  ]\n}',
      actualOutput: '',
      didPass: false
    },
    {
      id: 'prettyJsonString',
      category: 'String',
      comments: 'Pretty prints a JSON string',
      data: JSON.stringify({ json: '{"foo":1,"bar":[2,3]}' }, null, 2),
      template: '{{json | prettyJson(4)}}',
      expectedOutput: '{\n    "foo": 1,\n    "bar": [\n        2,\n        3\n    ]\n}',
      actualOutput: '',
      didPass: false
    }
  ];

  readonly tests = signal<TextTemplateTestCase[]>(this.initialTests.map(test => {
    // For prettyJsonObject, inject fooBar as an object
    if (test.id === 'prettyJsonObject') {
      return {
        ...test,
        data: JSON.stringify({ fooBar: { foo: 1, bar: [2, 3] } }, null, 2)
      };
    }
    return test;
  }));
  readonly markdownReport = signal<string>('');

  readonly runButtonConfig: ButtonConfig = {
    id: 'run-tt-tests',
    buttonText: 'Run tests',
    severity: 'primary',
    onClick: () => this.runTests()
  };

  readonly copyButtonConfig: ButtonConfig = {
    id: 'copy-tt-report',
    buttonText: 'Copy markdown',
    severity: 'secondary',
    onClick: () => this.copyMarkdown()
  };

  constructor(private readonly textTemplateService: EzTextService) {
    this.runTests();
  }

  runTests(): void {
    const updated = this.tests().map(test => {
      let actual = '';
      let didPass = false;

      try {
        const context = JSON.parse(test.data);
        actual = this.textTemplateService.resolveTemplate(test.template, context);
        didPass = actual === test.expectedOutput;
      } catch (error) {
        actual = `Error: ${(error as Error).message ?? error}`;
        didPass = false;
      }

      return { ...test, actualOutput: actual, didPass };
    });

    this.tests.set(updated);
    this.markdownReport.set(this.createMarkdown(updated));
  }

  async copyMarkdown(): Promise<void> {
    const report = this.markdownReport();
    if (!report) {
      return;
    }

    try {
      await navigator.clipboard.writeText(report);
    } catch (error) {
      console.warn('Unable to copy markdown report.', error);
    }
  }

  private createMarkdown(tests: TextTemplateTestCase[]): string {
    const header =
      '| # | Comments | Template | Data | Expected | Actual | Pass |\n' +
      '|:--|:--|:--|:--|:--|:--|:--|\n';

    let markdown = header;
    const categories: ('String' | 'Number' | 'DateTime')[] = ['String', 'Number', 'DateTime'];
    let rowIndex = 1;

    categories.forEach(category => {
      const filtered = tests.filter(t => t.category === category);
      if (filtered.length) {
        // Add a category sub-header row spanning all columns
        //markdown += `| **${category}** |  |  |  |  |  |  |\n`;
        markdown += `| **${category}**   |\n`;  // <-- even though only one column, when rendered still shows vertical lines.. no biggie.
        filtered.forEach(test => {
          const passEmoji = test.didPass ? '?' : '?';
          markdown += [
            `| ${rowIndex++}`,
            this.escapeCell(test.comments),
            this.escapeCell(test.template),
            this.escapeCell(this.compactJson(test.data)),
            this.escapeCell(test.expectedOutput),
            this.escapeCell(test.actualOutput),
            `${passEmoji} |`
          ].join(' | ') + '\n';
        });
      }
    });

    return markdown.trim();
  }

  private escapeCell(value: string): string {
    return value
      .replace(/\|/g, '\\|')
      .replace(/\r?\n/g, '<br>');
  }

  private compactJson(value: string): string {
    try {
      return JSON.stringify(JSON.parse(value));
    } catch {
      return value.replace(/\s+/g, ' ').trim();
    }
  }
}

